'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Cropper, type CropperRef } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'
import { useCropperStore } from '@/stores/cropperStore'
import type { CropArea } from './types'
import { getAspectRatioValue } from './utils/image'

interface ImageEditorProps {
  imageSrc: string
}

export function ImageEditor({ imageSrc }: ImageEditorProps) {
  const cropperRef = useRef<CropperRef>(null)
  const prevAspectRatioRef = useRef<number | undefined>(undefined)

  const currentTransform = useCropperStore((state) => state.currentTransform)
  const setCrop = useCropperStore((state) => state.setCrop)
  const setZoom = useCropperStore((state) => state.setZoom)
  const initializeCrop = useCropperStore((state) => state.initializeCrop)
  const setImageDimensions = useCropperStore((state) => state.setImageDimensions)
  const setCropperRef = useCropperStore((state) => state.setCropperRef)

  const aspectRatioValue = getAspectRatioValue(currentTransform.aspectRatio)

  useEffect(() => {
    setCropperRef(cropperRef.current)
  }, [setCropperRef])

  const onCropComplete = useCallback(
    (cropper: CropperRef) => {
      const coordinates = cropper.getCoordinates()
      if (coordinates) {
        const cropArea: CropArea = {
          x: coordinates.left,
          y: coordinates.top,
          width: coordinates.width,
          height: coordinates.height,
        }
        setCrop(cropArea)
      }
    },
    [setCrop],
  )

  const onReady = useCallback(
    (cropper: CropperRef) => {
      setCropperRef(cropper)

      const image = cropper.getImage()
      if (image?.src) {
        const img = new Image()
        img.onload = () => {
          setImageDimensions(img.naturalWidth, img.naturalHeight)
        }
        img.src = image.src
      }

      const coordinates = cropper.getCoordinates()
      if (coordinates) {
        const cropArea: CropArea = {
          x: coordinates.left,
          y: coordinates.top,
          width: coordinates.width,
          height: coordinates.height,
        }
        initializeCrop(cropArea)
      }
    },
    [initializeCrop, setImageDimensions, setCropperRef],
  )

  useEffect(() => {
    if (cropperRef.current && aspectRatioValue !== prevAspectRatioRef.current) {
      const cropper = cropperRef.current
      const state = cropper.getState()

      if (state && aspectRatioValue !== undefined) {
        cropper.refresh()
      }

      prevAspectRatioRef.current = aspectRatioValue
    }
  }, [aspectRatioValue])

  const [transformedImageSrc, setTransformedImageSrc] = useState<string>(imageSrc)

  useEffect(() => {
    const applyTransforms = async () => {
      if (currentTransform.rotation === 0 && !currentTransform.flipHorizontal && !currentTransform.flipVertical) {
        setTransformedImageSrc(imageSrc)
        return
      }

      try {
        const img = new Image()
        img.crossOrigin = 'anonymous'

        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve()
          img.onerror = reject
          img.src = imageSrc
        })

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          setTransformedImageSrc(imageSrc)
          return
        }

        const { width: canvasWidth, height: canvasHeight } = getRotatedDimensions(
          img.width,
          img.height,
          currentTransform.rotation,
        )
        canvas.width = canvasWidth
        canvas.height = canvasHeight

        ctx.save()
        ctx.translate(canvasWidth / 2, canvasHeight / 2)

        if (currentTransform.rotation !== 0) {
          ctx.rotate((currentTransform.rotation * Math.PI) / 180)
        }

        ctx.scale(currentTransform.flipHorizontal ? -1 : 1, currentTransform.flipVertical ? -1 : 1)

        ctx.drawImage(img, -img.width / 2, -img.height / 2, img.width, img.height)
        ctx.restore()

        const dataURL = canvas.toDataURL('image/jpeg', 0.95)
        setTransformedImageSrc(dataURL)
      } catch (error) {
        console.error('Error applying transforms:', error)
        setTransformedImageSrc(imageSrc)
      }
    }

    applyTransforms()
  }, [imageSrc, currentTransform.rotation, currentTransform.flipHorizontal, currentTransform.flipVertical])

  const getRotatedDimensions = (width: number, height: number, rotation: number) => {
    const normalizedRotation = ((rotation % 360) + 360) % 360
    if (normalizedRotation === 90 || normalizedRotation === 270) {
      return { width: height, height: width }
    }
    return { width, height }
  }

  const cropperStyle = useMemo(
    () => ({
      maxHeight: '650px',
      maxWidth: '100%',
      width: '100%',
      height: '100%',
    }),
    [],
  )
  const cropperStencilProps = useMemo(
    () => ({
      aspectRatio: aspectRatioValue,
      resizable: true,
      movable: true,
      grid: true,
    }),
    [aspectRatioValue],
  )

  return (
    <div className='w-full h-full flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden px-2 py-2 sm:px-4 sm:py-4 lg:px-8 lg:py-4'>
      <div
        className='max-h-[650px] max-w-full flex items-center justify-center'
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <Cropper
          ref={cropperRef}
          src={transformedImageSrc}
          className='cropper'
          style={cropperStyle}
          stencilProps={cropperStencilProps}
          onReady={onReady}
          onChange={onCropComplete}
          checkOrientation={false}
        />
      </div>
    </div>
  )
}
